import React, { useEffect, useRef } from 'react';
import { Button } from './Button';
import { SearchInput } from './SearchInput';
import { Select } from './Select';
import './ControlBar.css';

export interface ControlBarAction {
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
}

export interface ControlBarOption {
  value: string;
  label: string;
}

export interface ControlBarProps {
  searchEnabled?: boolean;
  searchQuery?: string;
  searchPlaceholder?: string;
  onSearchChange?: (query: string) => void;
  searchActions?: React.ReactNode;
  sortEnabled?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  sortOptions?: ControlBarOption[];
  onSortChange?: (sortBy: string) => void;
  onSortDirectionToggle?: () => void;
  selectEnabled?: boolean;
  selectedCount?: number;
  totalCount?: number;
  onSelectAll?: (checked: boolean) => void;
  filters?: React.ReactNode;
  actions?: ControlBarAction[];
  stacked?: boolean;
  density?: 'default' | 'compact';
}

export const ControlBar: React.FC<ControlBarProps> = ({
  searchEnabled = true,
  searchQuery = '',
  searchPlaceholder = 'Search...',
  onSearchChange,
  searchActions,
  sortEnabled = true,
  sortBy = 'name',
  sortDirection = 'asc',
  sortOptions = [],
  onSortChange,
  onSortDirectionToggle,
  selectEnabled = false,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  filters,
  actions = [],
  stacked = false,
  density = 'default',
}) => {
  const allSelected = selectedCount > 0 && selectedCount === totalCount;
  const someSelected = selectedCount > 0 && selectedCount < totalCount;
  const checkboxRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const className = [
    'ui-control-bar',
    stacked ? 'ui-control-bar--stacked' : '',
    density === 'compact' ? 'ui-control-bar--compact' : '',
    filters ? 'ui-control-bar--has-filters' : '',
    actions.length > 0 ? 'ui-control-bar--has-actions' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <div className="ui-control-bar__cluster ui-control-bar__cluster--primary">
        {selectEnabled && (
          <div className="ui-control-bar__selection">
            <input
              ref={checkboxRef}
              type="checkbox"
              checked={allSelected}
              onChange={(event) => onSelectAll?.(event.target.checked)}
            />
            {selectedCount > 0 && (
              <span className="ui-control-bar__selection-label">{selectedCount} selected</span>
            )}
          </div>
        )}

        {searchEnabled && (
          <div className="ui-control-bar__search">
            <SearchInput
              fullWidth
              density={density === 'compact' ? 'compact' : 'default'}
              value={searchQuery}
              placeholder={searchPlaceholder}
              onChange={(event) => onSearchChange?.(event.target.value)}
              onClear={() => onSearchChange?.('')}
            />
          </div>
        )}

        {searchActions ? <div className="ui-control-bar__actions-inline">{searchActions}</div> : null}
      </div>

      <div className="ui-control-bar__cluster ui-control-bar__cluster--secondary">
        {sortEnabled && sortOptions.length > 0 && (
          <div className="ui-control-bar__sort">
            <span className="ui-control-bar__label">Sort</span>
            <div className="ui-control-bar__sort-controls">
              <Select
                aria-label="Sort"
                fullWidth={stacked}
                density={density === 'compact' ? 'compact' : 'default'}
                options={sortOptions}
                value={sortBy}
                onChange={(event) => onSortChange?.(event.target.value)}
              />
              {onSortDirectionToggle ? (
                <button
                  type="button"
                  className="ui-control-bar__sort-direction"
                  onClick={onSortDirectionToggle}
                  aria-label={sortDirection === 'asc' ? 'Reverse sort to descending order' : 'Reverse sort to ascending order'}
                  title={sortDirection === 'asc' ? 'Ascending order. Click to reverse.' : 'Descending order. Click to reverse.'}
                >
                  <span className="ui-control-bar__sort-direction-icon" aria-hidden="true">
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                  <span>{sortDirection === 'asc' ? 'Asc' : 'Desc'}</span>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {filters ? <div className="ui-control-bar__filters">{filters}</div> : null}

        {actions.length > 0 && (
          <div className="ui-control-bar__actions">
            {actions.map((action, index) => (
              <Button
                key={`${action.label}-${index}`}
                type="button"
                variant={action.variant === 'danger' ? 'danger' : action.variant || 'secondary'}
                size={density === 'compact' ? 'sm' : 'md'}
                shape="pill"
                onClick={action.onClick}
                disabled={action.disabled}
                iconBefore={action.icon ? <span>{action.icon}</span> : undefined}
                fullWidth={stacked && actions.length === 1}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ControlBar.displayName = 'ControlBar';
